/**
 * Shared "email this invoice/quote as a PDF" transport.
 *
 * Extracted from `share-document-modal.tsx` so BOTH entry points ride the exact
 * same path:
 *   • the Share modal (send to one or more chosen recipients), and
 *   • the "Send to myself" quick action (send to the signed-in user's own inbox).
 *
 * It `POST`s the recipients, subject and note to `/api/v1/invoices/:id/share`.
 * It sends NO PDF: crm-api (`Api::V1::InvoicesController#share` →
 * `DocumentMailer#share_document`) attaches the document rendered by
 * `InvoicePdfRenderer`, the one generator — so the shared file is the one the
 * CRM previews and the finance review copy carries (2026-09-17; this used to
 * draw its own PDF in the browser and upload it). Each recipient gets the
 * deliberately-minimal "<sharer> shared this invoice with you" note + the PDF —
 * see docs/crm.md → "Share invoice/quote email is minimal".
 */
import api from "@crm-src/lib/api"

/** A share target. Only `email` is required; `name` (when present) shows in the
 *  greeting. crm-api reads `recipient["email"]`/`["name"]` off each row. Extra
 *  fields (id/type/company on the modal's `EmailRecipient`) are harmless. */
export interface ShareRecipient {
  email: string
  name?: string
}

export interface SendSharedDocumentOptions {
  documentId: string
  recipients: ReadonlyArray<ShareRecipient>
  subject: string
  body: string
}

export interface SendSharedDocumentResult {
  success?: boolean
  message?: string
  sent_count?: number
  failed_count?: number
  errors?: string[]
  error?: string
}

/**
 * Email document `documentId` to `recipients` via crm-api's share endpoint.
 * Resolves with the parsed API body (check `.success`); rejects the same way
 * axios would on a network/HTTP error so callers can surface the server's
 * reason.
 */
export async function sendSharedDocument(
  opts: SendSharedDocumentOptions,
): Promise<SendSharedDocumentResult> {
  const { documentId, recipients, subject, body } = opts

  const response = await api.post(`/invoices/${documentId}/share`, {
    recipients,
    subject,
    body,
  })

  return response.data as SendSharedDocumentResult
}
